import { useState, useEffect } from "react";
import {
  ChevronDownIcon,
  ChevronRightIcon,
  // ScanFrameIcon,
  SuccessCelebrationIcon,
} from "../../icons";
import { useDispatch, useSelector } from "react-redux";
import { AssetSelectDropdown, CryptoAccountSelectDropdown } from "../common/SelectDropdown";
import { GetClientAvailableAssets } from "../../../redux/actions/CryptoTransaction/CryptoAssets";
import { addCryptoBeneficiary } from "../../../redux/actions/CryptoTransaction/CryptoBeneficiary";
import LoadingSpinner from "../../../components/Common/LoadingSpinner";
import CryptoBottomSheet from "../CryptoBottomSheet";
import ErrorMessage from "../../../components/Common/ErrorMessage";
import useVASPList from "../../../hooks/useVASPList";
import { useForm } from "../../../hooks";
import { FormInput } from "../common/FormInput";
import TravelRuleInputs from "./TravelRuleInputs";
import JsQRScanner from "../../../components/ORScanner";
import { QRCodeIcon } from "../../../components/Icons";


const BeneficiarySavedModal = ({
  isOpen,
  onClose,
  onContinue,
  description = "Your beneficiary details have been successfully saved and are ready for transactions once approved.",
}) => (
  <CryptoBottomSheet
    isOpen={isOpen}
    onClose={onClose}
    showHeader={false}
    contentClassName="overflow-y-auto hide-scrollbar px-6 pb-6 pt-10"
  >
    <div className="flex flex-col items-center text-center">
      {/* Celebration / Checkmark Icon */}
      <SuccessCelebrationIcon />

      {/* Title & Description Group */}
      <div className="mt-4 flex flex-col items-center gap-1.5">
        <h3 className="text-xl font-semibold text-text-primary">
          Beneficiary Saved
        </h3>

        <p className="px-10 text-sm font-normal leading-relaxed text-text-secondary">
          {description}
        </p>
      </div>

      {/* Action Button */}
      <button
        type="button"
        onClick={onContinue}
        className="mt-6 flex h-12 w-full items-center justify-center rounded-[10px] bg-[#4ab933] text-sm font-semibold text-white transition-colors hover:bg-[#3fa82c]"
      >
        Continue
      </button>
    </div>
  </CryptoBottomSheet>
);

const AddCryptoBeneficiary = ({ onBack, onContinue }) => {
  const [loading, setLoading] = useState(false);
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [selectedAsset, setSelectedAsset] = useState(null);
  const { client_id } = useSelector((state) => state.userInfo.userInfo);
  const cryptoAccounts = useSelector((state) => state.client.clientInfo.crypto_accounts);
  const [showSuccess, setShowSuccess] = useState(false);
  const [error, setError] = useState("");

  const vaspOptions = useVASPList(); // Custom hook returning VASP list
  
  const [assets, setAssets] = useState([]);
  const [filterAssets, setFilterAssets] = useState([]);
  const destinationTag = useForm("");
  const walletAddress = useForm("");

  // Form State Hooks (Assuming a custom useForm hook pattern)
  const declareWallet = useForm(false);
  const selectedVASP = useForm("");
  const beneficiaryType = useForm("");
  const beneficiaryFirstName = useForm("");
  const beneficiaryLastName = useForm("");
  const businessLegalName = useForm("");

  const dob = useForm("");
  const countryCode = useForm("");

  const beneficiaryVASPName = useForm("");
  const walletType = useForm("");
  const sendToMyself = useForm(false);
  const VASPNotInList = useForm(false);

  const dispatch = useDispatch();
  // const navigate = useNavigate();

  const getClientAssets = async () => {
    try {
      setError("");

      const res = await dispatch(GetClientAvailableAssets(client_id));

      if (Array.isArray(res)) {
        setAssets(res);
      } else {
        throw new Error("Unable to load assets.");
      }
    } catch (err) {
      setError(err?.message || "Failed to load assets.");
    }
  };

  useEffect(() => {
    if (client_id) {
      getClientAssets();
    }
    // eslint-disable-next-line
  }, [client_id]);

  const canContinue = walletAddress.value.trim().length > 0 && selectedAccount && selectedAsset;

  const handleContinue = async () => {
    const check = validateBeneficiaryFields();
    if (!canContinue || loading || check) return;

    try {
      setLoading(true);
      setError("");

      const payload = {
        account_id: selectedAccount.id,
        network_id: selectedAsset.network_id,
        asset_id: selectedAsset.id,
        wallet_address: walletAddress.value.trim(),
        destination_tag: destinationTag.value,
        asset_type: "crypto",

        // Travel Rules
        beneficiary_type: beneficiaryType.value,
        wallet_type: walletType.value,
        beneficiary_first_name: beneficiaryFirstName.value,
        beneficiary_last_name: beneficiaryLastName.value,
        beneficiary_legal_name: businessLegalName.value,
        dob: dob.value,
        country_code: countryCode?.value?.iso2,
        is_not_on_vasp_list: VASPNotInList.value,
        is_ownership_declaration: declareWallet.value,
        is_sending_to_myself: sendToMyself.value,
      };

      if (!VASPNotInList.value) {
        payload["beneficiary_vasp_did"] = selectedVASP.value.value;
        payload["beneficiary_vasp_name"] = selectedVASP.value.label;
      } else {
        payload["beneficiary_vasp_name"] = beneficiaryVASPName.value;
      }

      const res = await dispatch(addCryptoBeneficiary(payload));
      
      if (res?.success) {
        setShowSuccess(true);
        return;
      }

      throw new Error(
        res?.message || "Unable to add beneficiary."
      );
    } catch (err) {
      setError(err?.message || "Something went wrong.");
    } finally {
      setLoading(false);
    }
  };

  const validateBeneficiaryFields = () => {
    let hasError = false;

    const setErrorFlag = (setter, message = true) => {
      hasError = true;
      setter(message);
    };

    // Validation Logic
    if (!beneficiaryType.value) {
      setErrorFlag(beneficiaryType.setError, "Please select beneficiary type");
    }
    
    if (beneficiaryType.value === "INDIVIDUAL") {
      if (!beneficiaryFirstName.value) {
        setErrorFlag(
          beneficiaryFirstName.setError,
          "Please enter beneficiary first name"
        );
      }

      if (!beneficiaryLastName.value) {
        setErrorFlag(
          beneficiaryLastName.setError,
          "Please enter beneficiary last name"
        );
      }
    }

    if (beneficiaryType.value === "BUSINESS" && !businessLegalName.value && !sendToMyself.value) {
      setErrorFlag(businessLegalName.setError, "Please enter business legal name");
    }

    if (!walletType.value) {
      setErrorFlag(walletType.setError, "Please select wallet type");
    }

    // if (walletType.value === "SELF_HOSTED" && !declareWallet.value) {
    if (sendToMyself.value && !declareWallet.value) {
      setErrorFlag(declareWallet.setError, "Please confirm ownership of this wallet");
    }

    if (walletType.value === "CUSTODIAL") {
      if (!VASPNotInList.value && (!selectedVASP?.value?.value || !selectedVASP.value.label)) {
        setErrorFlag(selectedVASP.setError, "Please select beneficiary VASP");
      }

      if (VASPNotInList.value && !beneficiaryVASPName.value) {
        setErrorFlag(
          beneficiaryVASPName.setError,
          "Please enter beneficiary VASP name"
        );
      }
    }

    // setError(hasError);
    return hasError;
  };

  const resetForm = () => {
    setSelectedAccount(null);
    setSelectedAsset(null);
    walletAddress.reset();
    destinationTag.reset();
    setShowSuccess(false);
    setLoading(false);
    walletAddress.reset();
    declareWallet.reset();
    selectedVASP.reset();
    beneficiaryType.reset();
    beneficiaryFirstName.reset();
    beneficiaryLastName.reset();
    businessLegalName.reset();
    dob.reset();
    countryCode.reset();
    beneficiaryVASPName.reset();
    walletType.reset();
    sendToMyself.reset();
    VASPNotInList.reset();
    setError("");

    onContinue();
  };

  return (
    <>
      {/* <div className={`mx-auto flex w-full max-w-[500px] min-h-[690px] flex-col pt-[17px] font-outfit hidden gap-10 sm:flex`}> */}
      <div className={`mx-auto w-full max-w-[500px] flex-col font-outfit hidden gap-10 sm:flex`}>
        <div className="relative flex w-full items-center justify-center">
          <button
            type="button"
            onClick={onBack}
            className="absolute left-0 top-0.5 flex size-6 items-center justify-center custom-transition hover:opacity-70"
            aria-label="Go back"
          >
            <ChevronDownIcon
              size={24}
              className="rotate-90 text-text-primary"
            />
          </button>
          <h2 className="text-center text-[20px] font-semibold leading-[28px] text-text-primary">
            Add Crypto Beneficiary
          </h2>
        </div>
        
        <div>
          {error && (
            <ErrorMessage
              message={error}
              onClose={() => setError("")}
            />
          )}
        </div>

        <div className="flex w-full flex-col gap-6">
          <div className="flex w-full flex-col gap-6">
            <CryptoAccountSelectDropdown
              label="Select Crypto Account"
              accounts={cryptoAccounts}
              value={selectedAccount}
              // onChange={setSelectedAccount}
              onChange={(account) => {
                setSelectedAccount(account);
                setFilterAssets(assets.filter(asset => asset.currency_id === account.currency_id));
                setSelectedAsset(null);
              }}
              filled
              disabled={loading}
            />
            
            <AssetSelectDropdown
              label="Select Asset"
              assets={filterAssets}
              placeholder="Select Asset"
              value={selectedAsset}
              onChange={setSelectedAsset}
              disabled={loading}
            />

            <FormInput
              label="Wallet Address"
              placeholder="Paste the address here"
              trailingIcon={
                <JsQRScanner onScan={walletAddress.setValue}>
                  <QRCodeIcon className="w-6 h-6 text-text-secondary cursor-pointer" />                  
                </JsQRScanner>
              }
              {...walletAddress}
              disabled={loading}
            />

            <div
              className={`grid transition-all duration-300 ease-in-out ${
                selectedAsset?.requires_tag
                  ? "grid-rows-[1fr] opacity-100"
                  : "grid-rows-[0fr] opacity-0 pointer-events-none"
              }`}
            >
              <div className="overflow-hidden">
                <div className="pt-2"> {/* Optional spacing wrapper so padding doesn't distort collapse */}
                  <FormInput
                    label="Destination Tag"
                    placeholder="Enter the Destination Tag"
                    disabled={loading}
                    {...destinationTag}
                  />
                </div>
              </div>
            </div>

          </div>
        </div>
      </div>

      <div className="flex flex-1 flex-col sm:hidden">
        <div className="relative flex items-center justify-center">
          <button
            type="button"
            onClick={onBack}
            className="absolute left-0 top-1/2 -translate-y-1/2 rounded-full p-0 custom-transition hover:bg-option-hover"
            aria-label="Go back"
          >
            <ChevronRightIcon className="rotate-180 text-text-secondary" />
          </button>
          <h1 className="text-base font-semibold leading-7 text-text-primary">
            Add Crypto Beneficiary
          </h1>
        </div>

        <div className="mt-6 flex flex-1 flex-col gap-6">
          <div className="flex flex-col gap-6">
            <CryptoAccountSelectDropdown
              label="Select Crypto Account"
              accounts={cryptoAccounts}
              value={selectedAccount}
              // onChange={setSelectedAccount}
              onChange={(account) => {
                setSelectedAccount(account);
                setFilterAssets(assets.filter(asset => asset.currency_id === account.currency_id));
                setSelectedAsset(null);
              }}
              filled
              disabled={loading}
            />
            
            <AssetSelectDropdown
              label="Select Asset"
              assets={filterAssets}
              placeholder="Select Asset"
              value={selectedAsset}
              onChange={setSelectedAsset}
              disabled={loading}
            />

            <FormInput
              label="Wallet Address"
              disabled={loading}
              placeholder="Paste the address here"
              {...walletAddress}
              trailingIcon={
                <JsQRScanner onScan={walletAddress.setValue}>
                  <QRCodeIcon className="w-6 h-6 text-text-secondary cursor-pointer" />
                </JsQRScanner>
              }
            />

            <div
              className={`grid transition-all duration-300 ease-in-out ${
                selectedAsset?.requires_tag
                  ? "grid-rows-[1fr] opacity-100"
                  : "grid-rows-[0fr] opacity-0 pointer-events-none"
              }`}
            >
              <div className="overflow-hidden">
                <div className="pt-2"> {/* Optional spacing wrapper so padding doesn't distort collapse */}
                  <FormInput
                    label="Destination Tag"
                    placeholder="Enter the Destination Tag"
                    disabled={loading}
                    {...destinationTag}
                  />
                </div>
              </div>
            </div>

          </div>
        </div>
      </div>
      
      <div className="mx-auto flex flex- w-full sm:max-w-[500px] flex-col font-outfit sm:gap-10 sm:flex">
        <TravelRuleInputs 
          vaspOptions={vaspOptions}
          declareWallet={declareWallet}
          selectedVASP={selectedVASP}
          beneficiaryType={beneficiaryType}
          beneficiaryFirstName={beneficiaryFirstName}
          beneficiaryLastName={beneficiaryLastName}
          businessLegalName={businessLegalName}
          beneficiaryVASPName={beneficiaryVASPName}
          dob={dob}
          countryCode={countryCode}
          walletType={walletType}
          sendToMyself={sendToMyself}
          VASPNotInList={VASPNotInList}
          setError={setError}
        />
        
        <button
          type="button"
          onClick={handleContinue}
          className="mt-auto flex h-12 w-full items-center justify-center rounded-[10px] text-sm font-semibold leading-[21px] text-white custom-transition enabled:bg-[#4ab933] enabled:hover:bg-[#3fa82c] disabled:cursor-not-allowed disabled:bg-[rgba(74,185,51,0.6)]"
        >
          {loading ? "loading...." : "Continue"}
        </button>
      </div>

      {loading && <LoadingSpinner />}

      {showSuccess && 
        <BeneficiarySavedModal
          isOpen={showSuccess}
          onClose={() => setShowSuccess(false)}
          onContinue={resetForm}
        />
      }
    </>
  );
};

export default AddCryptoBeneficiary;
